import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, FormControl, MenuItem, Paper, Select,
  TextField, Typography,
} from '@mui/material';
import AppDatatable from '../../components/AppDatatable';
import { getLoggedInUser } from '../../utils/permissions';
import { CLAIM_REPORTING_SOURCES, fetchClaimsReportingData, getClaimDate, normalizeAssignedLocations } from '../../utils/claimsReporting';

const STATUS_LABELS = { reported: 'Reported', processing: 'Processing', pendingClose: 'Pending Close', closed: 'Closed' };
const formatDate = (row) => getClaimDate(row)?.toLocaleDateString('en-CA') || '-';
const searchText = (value) => {
  if (value === null || value === undefined) return '';
  if (Array.isArray(value)) return value.map(searchText).join(' ');
  if (typeof value === 'object') return Object.values(value).map(searchText).join(' ');
  return String(value);
};
const displayLabel = (key) => String(key || '')
  .replace(/_/g, ' ')
  .replace(/([a-z])([A-Z])/g, '$1 $2')
  .replace(/\b\w/g, (letter) => letter.toUpperCase());
const displayValue = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') return JSON.stringify(value, null, 2);
  return String(value);
};

export default function SearchRecordsPage() {
  const baseURL = process.env.REACT_APP_API_BASE_URL;
  const user = useMemo(() => getLoggedInUser(), []);
  const assigned = useMemo(() => normalizeAssignedLocations(user?.selected_locations || user?.locations).map((item) => item.toLowerCase()), [user]);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [location, setLocation] = useState('All');
  const [workflow, setWorkflow] = useState('All');
  const [status, setStatus] = useState('All');
  const [query, setQuery] = useState('');
  const [appliedQuery, setAppliedQuery] = useState('');
  const [selectedRecord, setSelectedRecord] = useState(null);

  useEffect(() => {
    setLoading(true);
    fetchClaimsReportingData(baseURL)
      .then(setRecords)
      .catch(() => setError('Records could not be loaded.'))
      .finally(() => setLoading(false));
  }, [baseURL]);

  const scoped = useMemo(() => records.filter((row) => assigned.includes(row.reportingLocation.toLowerCase())), [assigned, records]);
  const locations = useMemo(() => [...new Set(scoped.map((row) => row.reportingLocation))].sort(), [scoped]);
  const filtered = useMemo(() => {
    const terms = appliedQuery.trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (!terms.length) return [];
    return scoped.filter((row) => {
      const haystack = searchText(row).toLowerCase();
      return (location === 'All' || row.reportingLocation === location)
        && (workflow === 'All' || row.reportingWorkflow === workflow)
        && (status === 'All' || row.reportingStage === status)
        && terms.every((term) => haystack.includes(term));
    });
  }, [appliedQuery, location, scoped, status, workflow]);
  const tableRows = filtered.map((row) => ({
    ...row,
    record_id: row._id,
    insurance: row.insurance_number || '-',
    rental_agreement: row.rentalAgreementNumber || row.ra_number || '-',
    driver: row.authorizedDriverName || row.customerName || '-',
    workflow: row.reportingWorkflow,
    stage: STATUS_LABELS[row.reportingStage],
    location: row.reportingLocation,
    record_date: formatDate(row),
  }));
  const columns = [
    { field: 'record_id', label: 'ID', sortable: true },
    { field: 'insurance', label: 'Insurance Number', sortable: true },
    { field: 'rental_agreement', label: 'Rental Agreement', sortable: true },
    { field: 'driver', label: 'Driver / Customer', sortable: true },
    { field: 'workflow', label: 'Module', sortable: true },
    { field: 'stage', label: 'Status', sortable: true },
    { field: 'location', label: 'Location', sortable: true },
    { field: 'record_date', label: 'Last Activity', sortable: true },
    { field: 'action', label: 'Action' },
  ];
  tableRows.forEach((row) => {
    row.action = <Button size="small" variant="outlined" onClick={() => setSelectedRecord(row)}>View</Button>;
  });
  const executeSearch = () => setAppliedQuery(query.trim());
  const clearSearch = () => { setQuery(''); setAppliedQuery(''); };
  const detailEntries = selectedRecord
    ? Object.entries(selectedRecord).filter(([key]) => !['action', '_rowSx'].includes(key))
    : [];

  return (
    <Box sx={{ ml: { md: '240px' }, mt: '64px', p: { xs: 2, md: 4 }, minHeight: 'calc(100vh - 64px)', bgcolor: '#f5f7fb' }}>
      <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 3 }}>
          <Box><Typography variant="h4" fontWeight={800} color="#123b76">Search Records</Typography><Typography color="text.secondary">Search claims across every workflow and stage in your assigned locations.</Typography></Box>
          {appliedQuery && <Chip color="primary" variant="outlined" label={`${filtered.length} result${filtered.length === 1 ? '' : 's'}`} />}
        </Box>
        <Box sx={{ p: 2, mb: 2, borderRadius: 2, bgcolor: '#f7f9fc', border: '1px solid #e6ebf2' }}>
          <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              autoFocus
              size="small"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => { if (event.key === 'Enter') executeSearch(); }}
              placeholder="Name, insurance ID, agreement number, VIN, unit, vehicle or any other information"
              sx={{ flex: '1 1 420px' }}
              inputProps={{ 'aria-label': 'Search claim records' }}
            />
            <Button variant="contained" onClick={executeSearch} disabled={!query.trim()}>Search</Button>
            <Button variant="text" onClick={clearSearch} disabled={!query && !appliedQuery}>Clear</Button>
          </Box>
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 2, mb: 3, p: 2, borderRadius: 2, bgcolor: '#f7f9fc' }}>
          <FormControl size="small"><Select value={location} onChange={(event) => setLocation(event.target.value)}><MenuItem value="All">All assigned locations</MenuItem>{locations.map((item) => <MenuItem key={item} value={item}>{item}</MenuItem>)}</Select></FormControl>
          <FormControl size="small"><Select value={workflow} onChange={(event) => setWorkflow(event.target.value)}><MenuItem value="All">All claim modules</MenuItem>{CLAIM_REPORTING_SOURCES.map((item) => <MenuItem key={item.key} value={item.workflow}>{item.workflow}</MenuItem>)}</Select></FormControl>
          <FormControl size="small"><Select value={status} onChange={(event) => setStatus(event.target.value)}><MenuItem value="All">All statuses</MenuItem>{Object.entries(STATUS_LABELS).map(([key, label]) => <MenuItem key={key} value={key}>{label}</MenuItem>)}</Select></FormControl>
        </Box>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {loading ? <Box py={8} textAlign="center"><CircularProgress /></Box> : !appliedQuery ? (
          <Box sx={{ py: 8, textAlign: 'center', color: 'text.secondary', border: '1px dashed #cbd5e1', borderRadius: 2 }}>
            <Typography fontWeight={700}>Enter record information to search</Typography>
            <Typography variant="body2">No records are displayed until a search is performed.</Typography>
          </Box>
        ) : (
          <AppDatatable data={{ rows: tableRows, columns }} hideSearch tableMinWidth={1180} noFoundMessage="No records match your search and selected filters." />
        )}
      </Paper>
      <Dialog open={Boolean(selectedRecord)} onClose={() => setSelectedRecord(null)} fullWidth maxWidth="lg">
        <DialogTitle sx={{ pb: 1 }}>
          Complete Record Information
          {selectedRecord && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1 }}>
              <Chip size="small" color="primary" label={selectedRecord.workflow} />
              <Chip size="small" label={selectedRecord.stage} />
              <Chip size="small" variant="outlined" label={selectedRecord.location} />
            </Box>
          )}
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2,minmax(0,1fr))' }, gap: 1.25 }}>
            {detailEntries.map(([key, value]) => (
              <Box key={key} sx={{ p: 1.5, border: '1px solid #e6ebf2', borderRadius: 1.5, bgcolor: '#fafbfd', minWidth: 0 }}>
                <Typography sx={{ mb: 0.5, fontSize: 11, fontWeight: 800, color: '#527096', textTransform: 'uppercase' }}>{displayLabel(key)}</Typography>
                <Typography component={typeof value === 'object' ? 'pre' : 'div'} sx={{ m: 0, fontFamily: typeof value === 'object' ? 'monospace' : 'inherit', fontSize: 13, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                  {displayValue(value)}
                </Typography>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions><Button onClick={() => setSelectedRecord(null)}>Close</Button></DialogActions>
      </Dialog>
    </Box>
  );
}
